import BusinessIcon from '@mui/icons-material/Business';
import {
  Avatar as MuiAvatar,
  AvatarProps as MuiAvatarProps,
  SxProps,
  Theme,
  Typography,
  TypographyTypeMap
} from '@mui/material';
import React, { ReactNode } from 'react';

/**
 * Primary UI component for user interaction
 */

const sizes = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'] as const;

type AvatarSizes = Record<
  (typeof sizes)[number],
  { styles: SxProps<Theme>; textVariant: TypographyTypeMap['props']['variant'] }
>;

const sizesValues: AvatarSizes = {
  xxs: {
    styles: {
      width: '24px',
      height: '24px',
      borderRadius: theme => theme.radius.xxs,
      '& svg': { width: '13.714px', height: '13.714px' }
    },
    textVariant: 'subheadingXSmall'
  },
  xs: {
    styles: {
      width: '32px',
      height: '32px',
      borderRadius: theme => theme.radius.xs,
      '& svg': { width: '19.2px', height: '19.2px' }
    },
    textVariant: 'labelXSmall'
  },
  s: {
    styles: {
      width: '40px',
      height: '40px',
      borderRadius: theme => theme.radius.xs,
      '& svg': { width: '24px', height: '24px' }
    },
    textVariant: 'labelSmall'
  },
  m: {
    styles: {
      width: '56px',
      height: '56px',
      borderRadius: theme => theme.radius.md,
      '& svg': { width: '33.6px', height: '33.6px' }
    },
    textVariant: 'labelLarge'
  },
  l: {
    styles: {
      width: '64px',
      height: '64px',
      borderRadius: theme => theme.radius.md,
      '& svg': { width: '40px', height: '40px' }
    },
    textVariant: 'labelLarge'
  },
  xl: {
    styles: {
      width: '80px',
      height: '80px',
      borderRadius: theme => theme.radius['2xl'],
      '& svg': { width: '48px', height: '48px' }
    },
    textVariant: 'h3'
  },
  xxl: {
    styles: {
      width: '120px',
      height: '120px',
      borderRadius: theme => theme.radius['2xl'],
      '& svg': { width: '72px', height: '72px' }
    },
    textVariant: 'h2'
  }
};

interface RoundedAvatarProps {
  /**
   * Size of the avatar
   */
  readonly size?: (typeof sizes)[number];
  /**
   * Src of the avatar
   * */
  readonly src?: string;
  /**
   * Initials of the avatar
   * @default ''
   * */
  readonly children?: ReactNode;
  /**
   * Custom styles
   * @default ''
   * */
  readonly sx?: MuiAvatarProps['sx'];
  /**
   * Custon icon in avatar
   * */
  readonly icon?: ReactNode;
}

export function RoundedAvatar({ size = 'xxs', children, sx, icon, ...props }: RoundedAvatarProps) {
  const styles = { ...sizesValues[size].styles, ...sx };

  return (
    <MuiAvatar sx={styles} variant="rounded" {...props}>
      {children && <Typography variant={sizesValues[size].textVariant}>{children}</Typography>}
      {!children && icon && <>{icon}</>}
      {!children && !icon && <BusinessIcon />}
    </MuiAvatar>
  );
}
